import * as THREE from 'three';
import { InputManager } from '../core/input';
import { PhysicsBody, createPlayerBody, updatePhysics, raycast } from '../core/physics';
import { PLAYER_SPEED, PLAYER_SPRINT_SPEED, PLAYER_EYE_HEIGHT, JUMP_VELOCITY, BLOCK_REACH } from '../constants';
import { clamp } from '../utils/math';
import { RaycastHit, GameSettings, DEFAULT_SETTINGS } from '../types';

export class Player {
  body: PhysicsBody;
  yaw = 0;
  pitch = 0;
  private camera: THREE.Camera;
  private input: InputManager;
  private settings: GameSettings;
  private _targetBlock: RaycastHit | null = null;

  constructor(camera: THREE.Camera, input: InputManager, x: number, y: number, z: number) {
    this.camera = camera;
    this.input = input;
    this.body = createPlayerBody(x, y, z);
    this.settings = { ...DEFAULT_SETTINGS };
  }

  update(
    dt: number,
    isSolid: (x: number, y: number, z: number) => boolean,
  ): void {
    if (!this.input.isPointerLocked && !this.input.isMobile) return;

    // Mouse look
    const [dx, dy] = this.input.getMouseDelta();
    this.yaw -= dx * this.settings.mouseSensitivity;
    this.pitch -= dy * this.settings.mouseSensitivity;
    this.pitch = clamp(this.pitch, -Math.PI / 2 + 0.01, Math.PI / 2 - 0.01);

    // Movement
    const forward = new THREE.Vector3(
      -Math.sin(this.yaw),
      0,
      -Math.cos(this.yaw),
    ).normalize();

    const right = new THREE.Vector3(
      Math.cos(this.yaw),
      0,
      -Math.sin(this.yaw),
    ).normalize();

    const moveDir = new THREE.Vector3(0, 0, 0);

    if (this.input.isKeyDown('KeyW')) moveDir.add(forward);
    if (this.input.isKeyDown('KeyS')) moveDir.sub(forward);
    if (this.input.isKeyDown('KeyD')) moveDir.add(right);
    if (this.input.isKeyDown('KeyA')) moveDir.sub(right);

    if (moveDir.lengthSq() > 0) {
      moveDir.normalize();
    }

    const sprinting = this.input.isKeyDown('ShiftLeft');
    const speed = sprinting ? PLAYER_SPRINT_SPEED : PLAYER_SPEED;

    this.body.vx = moveDir.x * speed;
    this.body.vz = moveDir.z * speed;

    // Jump
    if (this.input.isKeyDown('Space') && this.body.onGround) {
      this.body.vy = JUMP_VELOCITY;
      this.body.onGround = false;
    }

    // Physics
    updatePhysics(this.body, dt, isSolid);

    // Update camera
    this.camera.position.set(
      this.body.x,
      this.body.y + PLAYER_EYE_HEIGHT,
      this.body.z,
    );

    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch;

    // Raycast for target block
    const dir = new THREE.Vector3(0, 0, -1).applyEuler(this.camera.rotation);
    this._targetBlock = raycast(
      this.camera.position.x,
      this.camera.position.y,
      this.camera.position.z,
      dir.x, dir.y, dir.z,
      BLOCK_REACH,
      isSolid,
    );
  }

  get targetBlock(): RaycastHit | null {
    return this._targetBlock;
  }

  get eyePosition(): THREE.Vector3 {
    return new THREE.Vector3(this.body.x, this.body.y + PLAYER_EYE_HEIGHT, this.body.z);
  }

  get lookDirection(): THREE.Vector3 {
    return new THREE.Vector3(0, 0, -1).applyEuler(this.camera.rotation);
  }

  get position(): THREE.Vector3 {
    return new THREE.Vector3(this.body.x, this.body.y, this.body.z);
  }

  setPosition(x: number, y: number, z: number): void {
    this.body.x = x;
    this.body.y = y;
    this.body.z = z;
    this.body.vy = 0;
  }
}
